import React, { useEffect, useState } from "react";
import { Users, ClipboardList } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/AuthContext";
import { useNavigate } from "react-router-dom";
import { isAtLeast } from "@/lib/roles";
import { useFeaturePermissions } from "@/lib/FeaturePermissionContext";

export default function AdminUserManagementBanner() {
  const { effectiveRole, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [pendingRequests, setPendingRequests] = useState(0);
  const { canUseFeature } = useFeaturePermissions();
  useEffect(() => {
    if (!isAtLeast(effectiveRole, "admin")) return;
    let active = true;
    const load = async () => {
      try {
        if (currentUser?.id) await base44.auth.syncNow?.(currentUser.id);
        const list = await base44.entities.AccountRecoveryRequest.list("-requested_at");
        if (active) setPendingRequests((list || []).filter((r) => String(r.status || "pending").toLowerCase() === "pending").length);
      } catch { if (active) setPendingRequests(0); }
    };
    load();
    return () => { active = false; };
  }, [effectiveRole, currentUser?.id]);
  if (!isAtLeast(effectiveRole, "admin")) return null;
  if (!canUseFeature("user_management") && !canUseFeature("admin_requests")) return null;
  return (
    <div className="bg-slate-900 text-white rounded-3xl p-6 sm:p-8 flex flex-col justify-between gap-6 min-h-[170px] dark:bg-slate-800">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold tracking-tight">User management</h2>
        <p className="text-slate-300 mt-1 text-sm">Manage users, access levels and account recovery requests.</p>
      </div>
      <div className="flex flex-wrap gap-3 self-start">
        <Button onClick={() => navigate("/admin/user-management")} disabled={!canUseFeature("user_management")} className="bg-white text-slate-900 hover:bg-slate-100 rounded-xl gap-2">
          <Users className="w-4 h-4" /> View all users
        </Button>
        {effectiveRole === "admin" && canUseFeature("admin_requests") && <Button onClick={() => navigate("/admin/requests")} className="bg-white text-slate-900 hover:bg-slate-100 rounded-xl gap-2 relative">
          <ClipboardList className="w-4 h-4" /> Requests
          {pendingRequests > 0 && <span className="absolute -right-2 -top-2 min-w-5 h-5 px-1 rounded-full bg-red-600 text-white text-[11px] font-bold leading-5 text-center shadow-sm">{pendingRequests}</span>}
        </Button>}
      </div>
    </div>
  );
}
